import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, useHistory } from "react-router-dom";
import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Dialog } from "primereact/dialog";
import DashboardLayout from "../../layouts/DashboardLayout";
import StoreBreadcrumbs from "./components/StoreBreadcrumbs";
import DashboardSummaryStrip from "../../components/DashboardSummaryStrip/DashboardSummaryStrip";
import ProductTypeModal from "./components/ProductTypeModal/ProductTypeModal";
import ProductCardMenu from "./components/ProductCardMenu/ProductCardMenu";
import localeText from "../../../../locale";
import GetUserInfoService from "../../../../service/GetUserInfoService";
import OrderService from "../../../../service/OrderService";
import ProductService from "../../../../service/ProductService";
import { formatPrice } from "../../../../utilities/formatPrice";
import { AdvancedImage } from "@cloudinary/react";
import { fill } from "@cloudinary/url-gen/actions/resize";
import { cloudinaryInstance } from "../../../../utilities/Cloudinary/cloudinaryInstance";
import { getCurrencySymbol } from "../../../../utilities/CurrencySwitch";
import { PARTNER_MAX_PRODUCTS, SELLER_MAX_PRODUCTS } from "../../../../utilities/constants";
import { useToastContext } from "../../../../contexts/ToastContext/ToastContext";
import useGetUserExtrabonus from "../../../../hooks/useGetUserExtrabonus";
import { getLocale } from "../../../../utilities/locale/locale";
import "./StoreProductsPage.css";

const getUserInfoService = new GetUserInfoService();
const orderService = new OrderService();
const productService = new ProductService();

const isStoreSalesEnabled = (plan, userInfo) => {
  if (plan !== "Store") return true;
  const profilePlan = (userInfo?.plan || userInfo?.subscriptionType || "").toString();
  return (
    userInfo?.showCase === true ||
    userInfo?.showCase === "true" ||
    userInfo?.showcase === true ||
    userInfo?.showcase === "true" ||
    /startseller|seller/i.test(profilePlan)
  );
};

const getProductImageId = (product) => product?.imageid1 ?? product?.imageId1 ?? product?.imageid;

const VIEW_STORAGE_KEY = "__extraconomy_store_products_view";

const getProductRegistrationDate = (product) =>
  product?.date ?? product?.created ?? product?.createdAt ?? product?.creationDate ?? product?.data ?? null;

const formatRegistrationDate = (product) => {
  const raw = getProductRegistrationDate(product);
  if (!raw) return "—";
  const date = typeof raw === "number" && raw < 1e12 ? new Date(raw * 1000) : new Date(raw);
  if (Number.isNaN(date.getTime())) return "—";
  const localeTag = { pt: "pt-PT", en: "en-GB", it: "it-IT", es: "es-ES" }[getLocale()] || "pt-PT";
  return date.toLocaleDateString(localeTag, { day: "2-digit", month: "short", year: "numeric" });
};

const StatusBadge = ({ isPublished }) => (
  <span className={`products-status-badge ${isPublished ? "is-published" : "is-unpublished"}`}>
    <span className="products-status-dot" />
    {isPublished ? localeText("published") : localeText("unpublished")}
  </span>
);

const notifyProduct = async (product, username, token, notifyAllUsers, notifyMyEconomicCircle) => {
  return productService.postEditProduct(
    token,
    username,
    product.title,
    product.description || "",
    product.percentExtraCoin ?? product.percentExtracoin ?? 0,
    product.imageid1 ?? null,
    product.imageid2 ?? null,
    product.imageid3 ?? null,
    product.imageid4 ?? null,
    product.imageid5 ?? null,
    product.id,
    product.weight ?? 0,
    product.width ?? 0,
    product.height ?? 0,
    product.length ?? 0,
    product.subCategory?.id ?? null,
    null,
    null,
    null,
    null,
    !!product.service,
    !!product.downloadable,
    product.downloadtype || product.downloadType || null,
    product.cdnType ?? null,
    product.urldownload ?? null,
    product.urldownloadsample ?? null,
    product.shippingOption ?? null,
    product.conditionsUsedProduct ?? null,
    product.productState || product.stateofproduct || null,
    0,
    product.imageid6 ?? null,
    product.imageid7 ?? null,
    product.imageid8 ?? null,
    product.imageid9 ?? null,
    product.imageid10 ?? null,
    notifyAllUsers,
    notifyMyEconomicCircle,
  );
};

function ProductCard({
  product,
  storeUpgradeRequired,
  menuOpen,
  onToggleMenu,
  onPublishToggle,
  onEdit,
  onNotifyUsers,
  onNotifyCircle,
}) {
  const kebabRef = useRef(null);
  const imageId = getProductImageId(product);
  const isPublished = product.status === "PUBLISHED";

  return (
    <div className="col-12 sm:col-6 lg:col-4 xl:col-3">
      <div
        className="border-round p-2 flex flex-column justify-content-between relative"
        style={{
          height: "100%",
          backgroundColor: "color-mix(in srgb, var(--theme-border) 35%, transparent)",
        }}
      >
        <div>
          <div className="flex justify-content-between align-items-center p-1">
            <svg width="36" height="4" viewBox="0 0 36 4" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect
                width="16"
                height="4"
                rx="2"
                fill="color-mix(in srgb, var(--theme-text-muted) 55%, transparent)"
              />
              <rect
                x="22"
                width="4"
                height="4"
                rx="2"
                fill="color-mix(in srgb, var(--theme-text-muted) 35%, transparent)"
              />
              <rect
                x="32"
                width="4"
                height="4"
                rx="2"
                fill="color-mix(in srgb, var(--theme-text-muted) 35%, transparent)"
              />
            </svg>
            <div className="flex align-items-center relative">
            <button
              ref={kebabRef}
              type="button"
              className="product-card-kebab"
              onClick={(e) => {
                e.stopPropagation();
                if (!storeUpgradeRequired) onToggleMenu();
              }}
              aria-label={localeText("editProduct")}
              disabled={storeUpgradeRequired}
              style={{
                border: "none",
                background: "transparent",
                color: "var(--theme-text-muted)",
                cursor: storeUpgradeRequired ? "default" : "pointer",
                padding: "0.25rem 0.4rem",
              }}
            >
              <i className="pi pi-ellipsis-v" />
            </button>
            <ProductCardMenu
              visible={menuOpen}
              anchorRef={kebabRef}
              isPublished={isPublished}
              onClose={onToggleMenu}
              onPublishToggle={() => {
                onToggleMenu();
                onPublishToggle();
              }}
              onEdit={() => {
                onToggleMenu();
                onEdit();
              }}
              onNotifyUsers={() => {
                onToggleMenu();
                onNotifyUsers();
              }}
              onNotifyCircle={() => {
                onToggleMenu();
                onNotifyCircle();
              }}
            />
            </div>
          </div>
          <div className="flex justify-content-center align-items-center" style={{ minHeight: "160px" }}>
            {imageId ? (
              <AdvancedImage
                cldImg={cloudinaryInstance.image(imageId).resize(fill().width(300).height(200))}
                style={{ maxHeight: "160px", width: "100%", objectFit: "contain" }}
              />
            ) : (
              <i className="pi pi-image text-6xl text-400" />
            )}
          </div>
        </div>
        <div className="p-3">
          <h3 className="text-sm font-semibold m-0 mb-1 line-clamp-2" style={{ color: "var(--theme-text)" }}>
            {product.title || "—"}
          </h3>
          <p className="text-sm font-medium m-0" style={{ color: "var(--theme-text-muted)" }}>
            {getCurrencySymbol(product.currency || "EUR")} {formatPrice(product.priceUnique ?? 0)}
          </p>
          <span
            className="inline-block mt-2 text-xs px-2 py-1 border-round"
            style={{
              backgroundColor:
                isPublished
                  ? "var(--theme-primary)"
                  : "color-mix(in srgb, var(--theme-text-muted) 25%, transparent)",
              color: isPublished ? "#fff" : "var(--theme-text-muted)",
            }}
          >
            {isPublished ? localeText("published") : localeText("unpublished")}
          </span>
        </div>
      </div>
    </div>
  );
}

function ProductListRow({
  product,
  storeUpgradeRequired,
  menuOpen,
  onToggleMenu,
  onPublishToggle,
  onEdit,
  onNotifyUsers,
  onNotifyCircle,
}) {
  const kebabRef = useRef(null);
  const imageId = getProductImageId(product);
  const isPublished = product.status === "PUBLISHED";

  return (
    <div className="products-list-row">
      <div className="products-list-product">
        <div className="products-list-thumb">
          {imageId ? (
            <AdvancedImage
              className="products-list-thumb-img"
              cldImg={cloudinaryInstance.image(imageId).resize(fill().width(96).height(96))}
            />
          ) : (
            <i className="pi pi-image text-400" />
          )}
        </div>
        <p className="products-list-name" title={product.title || ""}>
          {product.title || "—"}
        </p>
      </div>
      <StatusBadge isPublished={isPublished} />
      <span className="products-list-value">
        {getCurrencySymbol(product.currency || "EUR")} {formatPrice(product.priceUnique ?? 0)}
      </span>
      <span className="products-list-date hidden md:block">{formatRegistrationDate(product)}</span>
      <div className="products-list-actions">
        <button
          type="button"
          className="products-list-action hidden md:block"
          onClick={onPublishToggle}
          disabled={storeUpgradeRequired}
          aria-label={localeText(isPublished ? "unpublishProduct" : "publishProduct")}
          title={localeText(isPublished ? "unpublishProduct" : "publishProduct")}
        >
          <i className={isPublished ? "pi pi-pause" : "pi pi-play"} />
        </button>
        <button
          type="button"
          className="products-list-action hidden md:block"
          onClick={onEdit}
          disabled={storeUpgradeRequired}
          aria-label={localeText("editProduct")}
          title={localeText("editProduct")}
        >
          <i className="pi pi-pencil" />
        </button>
        <button
          ref={kebabRef}
          type="button"
          className="products-list-action"
          onClick={(e) => {
            e.stopPropagation();
            if (!storeUpgradeRequired) onToggleMenu();
          }}
          disabled={storeUpgradeRequired}
          aria-label={localeText("editProduct")}
        >
          <i className="pi pi-ellipsis-v" />
        </button>
        <ProductCardMenu
          visible={menuOpen}
          anchorRef={kebabRef}
          isPublished={isPublished}
          onClose={onToggleMenu}
          onPublishToggle={() => {
            onToggleMenu();
            onPublishToggle();
          }}
          onEdit={() => {
            onToggleMenu();
            onEdit();
          }}
          onNotifyUsers={() => {
            onToggleMenu();
            onNotifyUsers();
          }}
          onNotifyCircle={() => {
            onToggleMenu();
            onNotifyCircle();
          }}
        />
      </div>
    </div>
  );
}

const StoreProductsPage = ({ plan, userInfo, productsRefreshTrigger }) => {
  const history = useHistory();
  const { showErrorToast, showSuccessToast } = useToastContext();
  const { userExtrabonus } = useGetUserExtrabonus({ onError: () => {}, typeUser: plan });
  const token = localStorage.getItem("__extraconomy_token");
  const username = localStorage.getItem("__extraconomy_username");

  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("recent");
  const [viewMode, setViewMode] = useState(() => {
    const stored = localStorage.getItem(VIEW_STORAGE_KEY);
    return stored === "list" || stored === "grid" ? stored : "grid";
  });
  const [openMenuId, setOpenMenuId] = useState(null);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [notifyDialog, setNotifyDialog] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const reloadProducts = async () => {
    const resProducts = await getUserInfoService.getProducts(token, username);
    if (resProducts?.messageCode === 200001 && Array.isArray(resProducts?.result)) {
      setProducts(resProducts.result.filter((p) => p.status !== "DELETED"));
    }
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [resProducts, resOrders] = await Promise.all([
          getUserInfoService.getProducts(token, username),
          orderService.getSales(token, username),
        ]);
        if (cancelled) return;
        if (resProducts?.messageCode === 200001 && Array.isArray(resProducts?.result)) {
          setProducts(resProducts.result.filter((p) => p.status !== "DELETED"));
        } else {
          setProducts([]);
        }
        if (resOrders?.messageCode === 200001 && Array.isArray(resOrders?.result)) {
          setOrders(resOrders.result.filter((o) => o.status === "RECEIVED"));
        } else {
          setOrders([]);
        }
      } catch {
        if (!cancelled) setProducts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [token, username, productsRefreshTrigger]);

  const totalProducts = products.length;
  const activeProducts = products.filter((p) => p.status === "PUBLISHED").length;
  const totalSales = useMemo(
    () => orders.reduce((acc, o) => acc + (Number(o.amountTotal ?? o.amountOrder ?? 0) || 0), 0),
    [orders],
  );

  const maxProductsForPlan = useMemo(() => {
    if (plan === "Seller") return SELLER_MAX_PRODUCTS;
    if (plan === "Partner") return PARTNER_MAX_PRODUCTS;
    return null;
  }, [plan]);
  const hasReachedProductLimit =
    typeof maxProductsForPlan === "number" && totalProducts >= maxProductsForPlan;
  const storeUpgradeRequired = !isStoreSalesEnabled(plan, userInfo);
  const disableProductActions = hasReachedProductLimit || storeUpgradeRequired;
  const productLimitReachedTooltip = hasReachedProductLimit
    ? (localeText("productLimitReached") || "").replace("{limit}", String(maxProductsForPlan))
    : undefined;

  const sortOptions = [
    { label: localeText("sortMostRecent"), value: "recent" },
    { label: localeText("sortOldest"), value: "oldest" },
    { label: localeText("sortHighestValue"), value: "highest" },
  ];

  const visibleProducts = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = query
      ? products.filter((p) => (p.title || "").toLowerCase().includes(query))
      : products;
    const sorted = [...filtered];
    if (sortBy === "oldest") sorted.sort((a, b) => (a.id || 0) - (b.id || 0));
    else if (sortBy === "highest")
      sorted.sort((a, b) => (Number(b.priceUnique) || 0) - (Number(a.priceUnique) || 0));
    else sorted.sort((a, b) => (b.id || 0) - (a.id || 0));
    return sorted;
  }, [products, search, sortBy]);

  const openNewProduct = () => {
    if (!disableProductActions && !storeUpgradeRequired) setShowTypeModal(true);
  };

  const changeViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem(VIEW_STORAGE_KEY, mode);
  };

  const handlePublishToggle = async (product) => {
    const nextStatus = product.status === "PUBLISHED" ? "UNPUBLISHED" : "PUBLISHED";
    setActionLoading(true);
    try {
      const res = await productService.postModifyStatus(token, product.id, nextStatus, username);
      if (res?.messageCode !== 200000) {
        showErrorToast({ code: res?.messageCode });
        return;
      }
      showSuccessToast({
        detail: nextStatus === "PUBLISHED" ? "productPublished" : "productUnpublished",
      });
      await reloadProducts();
    } catch {
      showErrorToast({ detail: "unexpectedErrorRegisterProduct" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleNotify = async () => {
    if (!notifyDialog) return;
    const needsBalance = notifyDialog.type === "users" ? 50 : 20;
    if ((userExtrabonus?.balance ?? 0) < needsBalance) {
      showErrorToast({ detail: "notifyInsufficientBalance" });
      setNotifyDialog(null);
      return;
    }
    setActionLoading(true);
    try {
      const res = await notifyProduct(
        notifyDialog.product,
        username,
        token,
        notifyDialog.type === "users",
        notifyDialog.type === "circle",
      );
      if (res?.messageCode !== 200000) {
        showErrorToast({ code: res?.messageCode });
        return;
      }
      showSuccessToast({ detail: "productNotified" });
    } catch {
      showErrorToast({ detail: "unexpectedErrorRegisterProduct" });
    } finally {
      setActionLoading(false);
      setNotifyDialog(null);
    }
  };

  return (
    <DashboardLayout>
      <StoreBreadcrumbs />
      <div className="flex align-items-center justify-content-between flex-wrap gap-3 mb-5 mt-4">
        <div className="flex align-items-center gap-4">
          <div
            className="border-circle p-3"
            style={{
              border: "1px solid var(--theme-border, #EBEBEB)",
              width: "54px",
              height: "54px",
            }}
          >
            <i className="pi pi-shopping-bag text-xl" style={{ color: "var(--theme-text-muted, #5C5C5C)" }} />
          </div>
          <div>
            <h2 className="text-lg font-semibold m-0" style={{ color: "var(--theme-text)" }}>
              {localeText("storeProducts")}
            </h2>
            <p className="text-sm m-0 mt-1" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
              {localeText("storePanelSubtitle")}
            </p>
          </div>
        </div>
        <>
          <Tooltip target=".new-product-btn-header-wrapper" />
          <span
            className="new-product-btn-header-wrapper"
            data-pr-tooltip={productLimitReachedTooltip}
            data-pr-position="top"
            style={{
              display: "inline-block",
              cursor: disableProductActions ? "not-allowed" : "auto",
            }}
          >
            <Button
              label={localeText("newProduct")}
              icon="pi pi-plus"
              className="border-round-md border-none p-no-outline"
              style={{
                backgroundColor: disableProductActions ? "#ccc" : "var(--theme-primary, #1DAF61)",
                color: "#fff",
                pointerEvents: disableProductActions ? "none" : "auto",
              }}
              onClick={storeUpgradeRequired ? undefined : openNewProduct}
              disabled={disableProductActions}
            />
          </span>
        </>
      </div>

      <DashboardSummaryStrip
        items={[
          { label: localeText("storeRegisteredArticles"), value: totalProducts },
          { label: localeText("published"), value: activeProducts },
          { label: localeText("storeRevenue"), value: <>€ {formatPrice(totalSales)}</> },
        ]}
        className="store-panel-summary"
      />

      {(hasReachedProductLimit || storeUpgradeRequired) && (
        <div
          className="flex align-items-center justify-content-between flex-wrap gap-3 border-round p-3 mt-3 mb-4"
          style={{
            border: "1px solid var(--theme-border, #EBEBEB)",
            backgroundColor: "color-mix(in srgb, var(--theme-border) 24%, transparent)",
          }}
        >
          <p className="m-0 text-sm" style={{ color: "var(--theme-text-muted)" }}>
            {storeUpgradeRequired
              ? localeText("storeActivateStartSellerWarning")
              : (localeText("storeProductsLimitWarning") || "").replace(
                  "{limit}",
                  String(maxProductsForPlan),
                )}
          </p>
          <Link to="/dashboard-upgrade" className="text-sm font-semibold no-underline">
            {localeText("storeActivateStartSellerCta")}
          </Link>
        </div>
      )}

      {loading ? (
        <div className="flex justify-content-center align-items-center py-8">
          <i className="pi pi-spin pi-spinner text-4xl text-500" />
        </div>
      ) : products.length === 0 ? (
        <div className="flex flex-column align-items-center justify-content-center py-8 gap-3 surface-50 border-round p-4">
          <i className="pi pi-inbox text-6xl text-400" />
          <p className="m-0" style={{ color: "var(--theme-text-muted)" }}>
            {localeText("noProducts") || "Nenhum produto registado."}
          </p>
          <Tooltip target=".new-product-btn-empty-wrapper" />
          <span
            className="new-product-btn-empty-wrapper"
            data-pr-tooltip={productLimitReachedTooltip}
            data-pr-position="top"
            style={{
              display: "inline-block",
              cursor: disableProductActions ? "not-allowed" : "auto",
            }}
          >
            <Button
              label={localeText("newProduct")}
              icon="pi pi-plus"
              className="border-round-md"
              onClick={storeUpgradeRequired ? undefined : openNewProduct}
              disabled={disableProductActions}
              style={{
                pointerEvents: disableProductActions ? "none" : "auto",
                backgroundColor: disableProductActions ? "#ccc" : "var(--theme-primary, #1DAF61)",
                color: "#fff",
              }}
            />
          </span>
        </div>
      ) : (
        <>
          <div className="flex align-items-center justify-content-between flex-wrap gap-3 mt-4 mb-4">
            <span className="p-input-icon-left" style={{ minWidth: "220px", flex: "1 1 240px" }}>
              <i className="pi pi-search" />
              <InputText
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={localeText("searchProductByName")}
                className="w-full border-round-lg"
              />
            </span>
            <div className="flex align-items-center gap-2">
              <div className="products-view-toggle" role="group">
                <button
                  type="button"
                  className={`products-view-toggle-btn ${viewMode === "grid" ? "is-active" : ""}`}
                  onClick={() => changeViewMode("grid")}
                  aria-label={localeText("productsViewGrid")}
                  aria-pressed={viewMode === "grid"}
                >
                  <i className="pi pi-th-large" />
                </button>
                <button
                  type="button"
                  className={`products-view-toggle-btn ${viewMode === "list" ? "is-active" : ""}`}
                  onClick={() => changeViewMode("list")}
                  aria-label={localeText("productsViewList")}
                  aria-pressed={viewMode === "list"}
                >
                  <i className="pi pi-bars" />
                </button>
              </div>
              <Dropdown
                value={sortBy}
                options={sortOptions}
                onChange={(e) => setSortBy(e.value)}
                placeholder={localeText("sortBy")}
                style={{ minWidth: "180px" }}
                className="border-round-lg p-no-outline"
              />
            </div>
          </div>
          {visibleProducts.length === 0 ? (
            <p className="text-center py-6" style={{ color: "var(--theme-text-muted)" }}>
              {localeText("noProductsFound")}
            </p>
          ) : viewMode === "list" ? (
            <div className="products-list-table">
              <div className="products-list-head">
                <span>{localeText("product")}</span>
                <span>{localeText("filterByStatus")}</span>
                <span>{localeText("productListValue")}</span>
                <span>{localeText("productListRegistration")}</span>
                <span />
              </div>
              {visibleProducts.map((p) => (
                <ProductListRow
                  key={p.id}
                  product={p}
                  storeUpgradeRequired={storeUpgradeRequired}
                  menuOpen={openMenuId === p.id}
                  onToggleMenu={() => setOpenMenuId((id) => (id === p.id ? null : p.id))}
                  onPublishToggle={() => handlePublishToggle(p)}
                  onEdit={() => history.push(`/dashboard-store/products/${p.id}/edit`)}
                  onNotifyUsers={() => setNotifyDialog({ product: p, type: "users" })}
                  onNotifyCircle={() => setNotifyDialog({ product: p, type: "circle" })}
                />
              ))}
            </div>
          ) : (
            <div className="grid">
              {visibleProducts.map((p) => (
                <ProductCard
                  key={p.id}
                  product={p}
                  storeUpgradeRequired={storeUpgradeRequired}
                  menuOpen={openMenuId === p.id}
                  onToggleMenu={() => setOpenMenuId((id) => (id === p.id ? null : p.id))}
                  onPublishToggle={() => handlePublishToggle(p)}
                  onEdit={() => history.push(`/dashboard-store/products/${p.id}/edit`)}
                  onNotifyUsers={() => setNotifyDialog({ product: p, type: "users" })}
                  onNotifyCircle={() => setNotifyDialog({ product: p, type: "circle" })}
                />
              ))}
            </div>
          )}
        </>
      )}

      <ProductTypeModal visible={showTypeModal} onHide={() => setShowTypeModal(false)} />

      <Dialog
        visible={!!notifyDialog}
        onHide={() => setNotifyDialog(null)}
        header={localeText("confirmation")}
        modal
        draggable={false}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button
              label={localeText("cancel")}
              className="p-button-outlined"
              onClick={() => setNotifyDialog(null)}
              disabled={actionLoading}
            />
            <Button
              label={localeText("confirm")}
              onClick={handleNotify}
              loading={actionLoading}
              style={{ backgroundColor: "var(--theme-primary)", border: "none" }}
            />
          </div>
        }
      >
        <p className="m-0">
          {notifyDialog?.type === "users"
            ? localeText("notifyConfirmUsers")
            : localeText("notifyConfirmCircle")}
        </p>
      </Dialog>
    </DashboardLayout>
  );
};

StoreProductsPage.defaultProps = {
  productsRefreshTrigger: 0,
};

export default StoreProductsPage;
